import {
  Avatar,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  Tooltip
} from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useSendListingMessage } from '../../../../../../hooks/listings/useSendListingMessage'
import { selectCustomerFirstName, selectCustomerPhotoUri } from '../../../../../../store/selectors/customerProfile'
import { MessageInput, MessageTopicKey } from '../../../../../../api/gqlaxy/gql/generated/graphql'
import * as Sentry from '@sentry/browser'
import { hasCustomerToken } from '../../../../../../auth'
import { useState } from 'react'

const MSG = defineMessages({
  cancel: {
    id: 'BuyDownOpenMessage.cancel',
    defaultMessage: 'Cancel'
  },
  success: {
    id: 'BuyDownOpenMessage.success',
    defaultMessage: 'Direct message sent!'
  },
  fail: {
    id: 'BuyDownOpenMessage.fail',
    defaultMessage: 'Direct message failed to send.'
  },
  impersonation: {
    id: 'BuyDownOpenMessage.impersonation',
    defaultMessage: 'Messaging not available when impersonating client'
  },
  messageLO: {
    id: 'BuyDownOpenMessage.messageLO',
    defaultMessage: 'Message {customerFirstName}'
  },
  send: {
    id: 'BuyDownOpenMessage.send',
    defaultMessage: 'Send'
  },
  haveAQuestion: {
    id: 'BuyDownOpenMessage.haveAQuestion',
    defaultMessage: 'Have questions?'
  },
  placeholder: {
    id: 'BuyDownOpenMessage.placeholder',
    defaultMessage: 'Can I get a buydown?'
  }
})

interface BuyDownOpenMessageProps {
  listingId?: string
}

export const BuyDownOpenMessage = ({ listingId }: BuyDownOpenMessageProps) => {
  const intl = useIntl()
  const {
    mutateAsync: sendListingMessage,
    isLoading: isSending,
    isSuccess,
    reset: resetSendListingMessage
  } = useSendListingMessage()

  const customerFirstName = useSelector(selectCustomerFirstName)
  const customerPhotoUri = useSelector(selectCustomerPhotoUri)

  const isImpersonating = hasCustomerToken()

  const [message, setMessage] = useState('')

  const handleSubmit = async (message: string) => {
    const input: MessageInput = {
      topicKey: MessageTopicKey.ListingsDirectMessageToSponsor,
      message: message || 'Can I get a buydown?',
      topic: 'buy downs as a seller concession'
    }

    if (listingId) {
      input.listingId = listingId
    }

    try {
      await sendListingMessage({
        input
      })
    } catch (error: any) {
      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: error.message || '',
          requestData: error.config?.data || {},
          requestErrors: Object.values(error.response?.data?.errors) || []
        }
      })
    }
  }

  return (
    <>
      <Flex justifyContent={'flex-start'} gap={2} alignItems={'center'} mb={4}>
        <Avatar src={customerPhotoUri} size={'sm'} />
        <Heading as={'h3'} size={'sm'} my='auto' variant={'eyebrow'}>
          {intl.formatMessage(MSG.haveAQuestion)}
        </Heading>
      </Flex>

      <Tooltip
        label={isImpersonating ? intl.formatMessage(MSG.impersonation) : null}
        aria-label={isImpersonating ? intl.formatMessage(MSG.impersonation) : null}
        textAlign={'center'}
        shouldWrapChildren
        hidden={!isImpersonating}
      >
        <Flex gap={2} width={'100%'} flexDir={['column', 'column', 'row']}>
          <FormControl flex={'1 1 auto'} minW={0}>
            <FormLabel size={'sm'}>{intl.formatMessage(MSG.messageLO, { customerFirstName })}</FormLabel>
            <Input
              type='text'
              name='message'
              onChange={e => {
                setMessage(e.target.value)
                if (isSuccess) {
                  resetSendListingMessage()
                }
              }}
              value={message}
              placeholder={intl.formatMessage(MSG.placeholder)}
              transition='all 0.5s ease-in-out'
            />
          </FormControl>
          <Button
            onClick={() => handleSubmit(message)}
            isLoading={isSending}
            transition='all 0.5s ease-in-out'
            alignSelf='flex-end'
            flex={'0 0 auto'}
            minW={'fit-content'}
            isDisabled={isSuccess || isImpersonating}
            colorScheme={'brand'}
            width={['100%', '100%', 'auto']}
          >
            {isSuccess && <Icon name='check-circle-fa' mr={2} />}
            {isSuccess ? intl.formatMessage(MSG.success) : intl.formatMessage(MSG.send)}
          </Button>
        </Flex>
      </Tooltip>
    </>
  )
}
